import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Camera,
  CheckCircle2,
  CloudUpload,
  SwitchCamera,
  Trash2,
} from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import sidebarArt from "@/assets/add-member-sidebar.svg";
import logo from "@/assets/logo-light.png";
import primaryMemberArt from "@/assets/primary-member.svg";
import supplementaryMemberArt from "@/assets/supplimentary-member.svg";
import { dataUrlToBlob, readPicture } from "@/components/avatar";
import { capturedName } from "@/components/avatar-picker";
import { type ConsentText, ConsentTextModal } from "@/components/consent-texts";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Checkbox, Field, Input, Label, Toggle } from "@/components/ui/field";
import { FkSelect } from "@/components/ui/fk-select";
import { NameInput } from "@/components/ui/name-input";
import { OptionSelect } from "@/components/ui/option-select";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip } from "@/components/ui/tooltip";
import { api, errorMessage } from "@/services/api";
import type { Translate, TranslationKey } from "@/constants/i18n";
import {
  TEMPORARY_CARD_DIGITS,
  sanitizeTemporaryCard,
  temporaryCardError,
} from "@/utils/temporary-card";
import { birthDateMin, today } from "@/utils/dates";
import { isValidMobile } from "@/utils/contact";
import { upperName } from "@/utils/names";
import { cn } from "@/utils/utils";
import {
  ADD_MEMBER_ACQUISITION,
  GENDER,
  type Member,
  PREFIX,
  SUFFIX,
  fullName,
} from "@/pages/MembersPage";
import { UnauthorizedPage } from "@/components/error-page";
import { profileQuery } from "@/services/queries";
import { can } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";
import { useTypedSearch } from "@/hooks/use-typed-search";

const TIERS = ["member", "affiliates", "employee", "complimentary"] as const;

/**
 * What each enrolment asks for. The same four codenames the Add Member menu
 * filters itself with, so the menu and the page cannot disagree about who may
 * take somebody on — and each stands alone: `add__member` is the ordinary
 * tier, not a base right the other three sit on top of.
 */
const TIER_PERMISSION: Record<(typeof TIERS)[number], string> = {
  member: "add__member",
  affiliates: "add__member_affiliates",
  employee: "add__member_employee",
  complimentary: "add__member_complimentary",
};
type Tier = (typeof TIERS)[number];

const searchSchema = z.object({
  tier: z.enum(TIERS).catch("member"),
  /** A primary's account number: the detail page sends a clerk here to add a supplementary. */
  parent: z.string().optional().catch(undefined),
});

type AccountType = "primary" | "supplementary";

/** Which of the three steps is on screen: who to add, a gate, the form. */
type Step = 1 | 2 | 3;

/**
 * The heading over each step of the flow.
 *
 * Legacy's `__step-title`: 36/44 and semibold, which is a step above the type
 * scale's largest. It is the only thing on the screen at the top of a step a
 * clerk has been sent to from somewhere else, and it says which of the four
 * they landed on; the scale's 26 reads as a section heading in a page rather
 * than the name of the page.
 */
/**
 * The step's own heading, at legacy's three sizes: 36px where there is room,
 * 28 under 1024 and 24 under 768 (`AddMember.scss`, `&__step-title` and its two
 * media rules). A 36px line over a 340px card is four words and a wrap.
 */
const STEP_TITLE =
  "text-heading text-2xl leading-8 font-semibold md:text-[1.75rem] md:leading-9 lg:text-[2.25rem] lg:leading-[2.75rem]";

/** A required field's label. One asterisk, one colour, everywhere. */
const req = (text: string) => (
  <>
    {text} <span className="text-danger">*</span>
  </>
);

/**
 * The tiers this screen only ever enrols as a primary, so the flow never asks.
 *
 * Two different reasons, and it is worth knowing which is which. An affiliate
 * and an employee have no supplementary card number to be given at all — the
 * matrix in the backend's `member/numbering.py` refuses the pair, so a
 * supplementary of either is not a member that can exist.
 *
 * A complimentary *can* have one (identifier 51, cards 51-97). It is not
 * offered here because a supplementary is added from the primary it hangs off
 * — the detail page's own Add Supplementary, which arrives with that account
 * already filled in — and this screen is reached from Add Member, where there
 * is no primary in hand to hang one off. Asking "primary or supplementary?"
 * only to send the second answer looking for an account number is a question
 * with one useful answer.
 */
const PRIMARY_ONLY: Tier[] = ["affiliates", "employee", "complimentary"];

/** The tiers enrolled as a benefit, never over a counter. */
const BY_BENEFIT: Tier[] = ["affiliates", "employee", "complimentary"];

/** Where a home address is part of the record. */
const NEEDS_ADDRESS: Tier[] = ["member", "employee"];

/** Nobody under this age can hold a membership. */
const MINIMUM_AGE = 18;

/**
 * What the reader made of a scanned ID card.
 *
 * Every field is what the card actually showed, or nothing — a blank is never
 * guessed at, so a blank here is a field the clerk types. The honorific,
 * suffix and gender already carry this API's own values, and the city and
 * barangay already carry our ids, so nothing here needs interpreting.
 */
interface IdCard {
  document_type: string | null;
  prefix: string | null;
  suffix: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  gender: string | null;
  date_of_birth: string | null;
  /** The ten digits after +63, as the mobile field holds them. */
  mobile_number: string | null;
  email: string | null;
  address: string | null;
  city_id: number | null;
  city_name: string | null;
  barangay_id: number | null;
  barangay_name: string | null;
  expiry_date: string | null;
}

/**
 * The fields an ID card owns.
 *
 * It fills them when it is scanned, they are wiped before a second card is
 * scanned over the first, and they go when the card is taken off — a form left
 * holding half of one person and half of another is the one outcome worth
 * ruling out. The two address lines are in the set because the address the
 * card gave is: keeping a unit number from a card that is no longer attached
 * is the same mistake in a smaller place.
 */
const CARD_FIELDS = [
  "prefix",
  "suffix",
  "first_name",
  "middle_name",
  "last_name",
  "date_of_birth",
  "gender",
  "mobile",
  "email",
  "address_street",
  "building_name",
  "unit_number",
  "city_id",
  "barangay_id",
] as const;

/**
 * Why a scan failed, in the words the counter needs.
 *
 * The server says which of the two it was in ``reason``, so the sentence is
 * chosen rather than read out of a message: "expired" is a real card that is
 * too old and the applicant needs a different one; anything else is a picture
 * worth taking again. A reader that is down or unset is neither — that is not
 * the applicant's card's fault and must not be reported as though it were.
 */
function cardProblem(error: unknown, t: Translate): string {
  const reason = axios.isAxiosError<{ reason?: string }>(error)
    ? error.response?.data?.reason
    : undefined;
  if (reason === "expired") return errorMessage(error, t("new.cardExpired"));
  if (reason === "unreadable") return t("new.cardUnreadable");
  return errorMessage(error, t("new.cardReaderDown"));
}

/** Every field a card owns, emptied. */
const blankCardFields = (): Record<string, string> =>
  Object.fromEntries(CARD_FIELDS.map((name) => [name, ""]));

/** What a read card sets on the form: what it showed, and nothing else. */
export function fieldsFromCard(card: IdCard): Record<string, string> {
  const filled: Record<string, string> = {};
  const put = (name: string, value: string | number | null) => {
    if (value !== null && value !== "") filled[name] = String(value);
  };
  put("prefix", card.prefix);
  put("suffix", card.suffix);
  // Through the same rule the keyboard goes through: a card read as "Dela
  // Cruz" must not enter the form differently from one typed as "DELA CRUZ".
  put("first_name", card.first_name && upperName(card.first_name));
  put("middle_name", card.middle_name && upperName(card.middle_name));
  put("last_name", card.last_name && upperName(card.last_name));
  put("date_of_birth", card.date_of_birth);
  put("gender", card.gender);
  put("mobile", card.mobile_number);
  put("email", card.email);
  put("address_street", card.address);
  put("city_id", card.city_id);
  // Only inside the city it was matched in — the barangay dropdown is filtered
  // by the city, and an id from another one would show as a number.
  if (card.city_id !== null) put("barangay_id", card.barangay_id);
  // A name that arrived means the card has one, so the "no last name" switch
  // goes back off even if it had been flicked on.
  if (card.last_name) filled["has_last_name"] = "true";
  return filled;
}

/** Whole years between a date and today; -1 for no date, so a blank is not 0. */
const years = (birthday: string | undefined) => {
  if (!birthday) return -1;
  const then = new Date(birthday);
  const now = new Date();
  const whole = now.getFullYear() - then.getFullYear();
  const before =
    now.getMonth() < then.getMonth() ||
    (now.getMonth() === then.getMonth() && now.getDate() < then.getDate());
  return before ? whole - 1 : whole;
};

/**
 * The primary a supplementary hangs off, as the gate needs to show it. The
 * verdict is the status code — this shape only ever comes back for an account
 * that can take another card.
 */
interface PrimaryAccount {
  member_id: number;
  account_number: string;
  first_name: string;
  middle_name: string | null;
  last_name: string | null;
  date_of_birth: string;
  valid_until: string | null;
  no_expiry: boolean;
  membership_tier: Tier;
  membership_type: string;
  supplementaries_in_use: number;
  supplementary_limit: number;
  /** Business primary with its free card still to give: the checkbox below. */
  free_supplementary_offered: boolean;
}

/**
 * The enrolment flow: who to add, a gate where one is owed, then the form.
 *
 * A gate is a step that asks one thing and will not open the form until the
 * server has answered — the primary a supplementary hangs off, the employee
 * number an employee membership is for. Everything else is a section of the
 * one form rather than a step of its own: a form the eye can see the whole of
 * beats a machine that reveals it a question at a time.
 */
function NewMemberPage() {
  const t = useTranslation();
  const { data: profile } = useQuery(profileQuery());
  const { tier, parent: parentNumber } = useTypedSearch(searchSchema);
  const navigate = useNavigate();
  // `PRIMARY_ONLY` is about the Add Member menu, where there is no primary in
  // hand to hang a card off. Arriving with one in the address bar is the other
  // way in — the detail page's own Add Supplementary — and that is a
  // supplementary whatever the tier, including the complimentary cards that
  // take identifiers 51-97.
  const primaryOnly = !parentNumber && PRIMARY_ONLY.includes(tier);
  const [accountType, setAccountType] = useState<AccountType | null>(
    primaryOnly ? "primary" : parentNumber ? "supplementary" : null,
  );
  // What each gate produced, and what the form then treats as given.
  const [parent, setParent] = useState<PrimaryAccount | null>(null);
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [hr, setHr] = useState<HrEmployee | null>(null);
  // Nothing to ask when only one answer is possible, so that flow starts later.
  const [step, setStep] = useState<Step>(
    primaryOnly ? (tier === "employee" ? 2 : 3) : parentNumber ? 2 : 1,
  );
  const cancel = () => void navigate("/members");

  // Which enrolment this is, and whether it is theirs to make.
  //
  // The address opens on any one of the four grants — that is all the router
  // can know, because the tier is in the query string. Here it is known, so
  // here is where the answer is: somebody holding affiliates alone gets the
  // affiliates form and a refusal on `?tier=employee`, rather than a form the
  // server would turn down at the end of it.
  //
  // A supplementary is its own grant whatever tier it goes under: it is taking
  // somebody on, and the tier is the primary's rather than a choice made here.
  // Refused only on an answer, and only on one that can be read.
  //
  // `can` says no to a profile that has not arrived, so refusing on its word
  // alone would flash Unauthorized at everybody while `/me` is in flight —
  // the route guard waits for the same reason, and the dashboard tiles stay
  // open until they know. And a profile that came back the wrong shape is not
  // a refusal either: `can` reads defensively so that a bad answer costs a
  // button rather than the page the button is on, and this is the page.
  const required = parentNumber
    ? "add__member_supplementary_history"
    : (TIER_PERMISSION[tier] ?? "add__member");
  const answered = Array.isArray(profile?.permissions);
  if (answered && !can(profile, required)) return <UnauthorizedPage />;

  return (
    /* The screen, once: the sidebar stands still and the form scrolls beside
       it, as legacy has it (`position: fixed; inset: 0` on the pair, with the
       scroll on the content alone). Scrolling the two together would carry the
       S&R mark and the line under it off the top of a form that is a good deal
       taller than the window. */
    <div className="grid h-dvh grid-rows-[11rem_minmax(0,1fr)] overflow-hidden md:grid-cols-[minmax(0,300px)_minmax(0,1fr)] md:grid-rows-none lg:grid-cols-[minmax(18rem,28%)_1fr]">
      {/* Legacy's three shapes for one panel (`AddMember.scss`): the column at
          full width, the same column capped at 300px under 1024, and under 768
          a 176px band across the top — `flex-direction: column` on the pair.
          It used to be `hidden` below 1024 instead, which is the one thing
          legacy never does: the artwork is how somebody knows they are on the
          enrolment and not in the members list, and a phone is where that
          matters most, having no other chrome left to say it. */}
      <aside className="bg-brand-700 relative overflow-hidden text-white">
        {/* The artwork is 512 by 960 and only its lower half holds anything:
            a photograph on a -15° slant under a blue gradient, with the rest
            flat #3141F1 and the S&R mark drawn into the corner at 52,52.
            A column shows all of it, so it is anchored top left there — what
            `cover` crops on a narrow sidebar is the sides, and the mark is in
            the corner it would take first.

            The band cannot. 176px of a 712px-tall render is a quarter of the
            picture, and taking it off the top is a quarter of flat blue —
            which is what the enrolment banner had become on a phone. Anchored
            to the bottom it is the photograph, which is legacy's own
            `object-position: left bottom` under 768 and the reason legacy
            draws its own logo there: the artwork's mark goes out of frame with
            everything else above the crop. */}
        <img
          src={sidebarArt}
          alt=""
          className="absolute inset-0 size-full object-cover object-left-bottom md:object-left-top"
        />
        {/* Legacy's `&__sidebar-overlay`: one column over the artwork, its
          contents against the left edge — 1.25rem in on the band, 1.5 under
          1024, 2.5 above. The mark and the line under it are two items in it
          rather than a picture and a margin, which is the whole of the
          alignment: the artwork's own mark sits at x=52 of a 512-wide canvas,
          so on a 380px band it landed 38px in while the line below it started
          at 20, and no padding could agree with both. */}
        <div className="absolute inset-0 flex flex-col items-start gap-3 px-5 pt-5 md:gap-0 md:px-6 md:pt-24 lg:px-10 lg:pt-40">
          {/* The band's own mark, since the artwork's is above the crop. Hidden
            from `md`, where the artwork carries it again — two marks, one of
            them ours, is the thing to avoid. 28px, as legacy sizes it. */}
          <img
            src={logo}
            alt="S&R"
            className="block h-7 w-auto max-w-[120px] object-contain md:hidden"
          />
          <p className="max-w-none text-[1rem] leading-[1.45] font-normal tracking-normal md:max-w-[240px] md:text-[1.25rem] md:leading-[1.6]">
            {t("new.taglineFor").replace(
              "{tier}",
              accountType === "supplementary"
                ? `${t(TIER_LABELS[tier])} ${t("mem.supplementary")}`
                : t(TIER_LABELS[tier]),
            )}
          </p>
        </div>
      </aside>

      <main className="bg-canvas relative min-h-0 overflow-y-auto px-5 py-8 md:px-8 md:py-10 lg:px-12">
        {/* 1rem in from both edges on a phone, 1.5/2 above — legacy's
            `&__cancel-btn`. The step below it starts low enough to clear it. */}
        <Button
          variant="secondary"
          onClick={cancel}
          className="absolute top-4 right-4 md:top-6 md:right-8"
        >
          {t("common.cancel")}
        </Button>
        {step === 1 ? (
          <WhoToAdd
            value={accountType}
            onChange={setAccountType}
            onContinue={() => setStep(accountType === "supplementary" ? 2 : 3)}
          />
        ) : step === 2 && accountType === "supplementary" ? (
          <PrimaryGate
            initial={parentNumber ?? ""}
            // Arrived with the clerk, so it is not theirs to change.
            locked={Boolean(parentNumber)}
            // Always the tier, from either way in.
            //
            // It used to be withheld when the number arrived in the address
            // bar, on the reasoning that a clerk coming from a member's own
            // page has that primary in hand and so has no tier to be wrong
            // about. The field is editable, so they have nothing of the sort:
            // the number can be replaced with any other account, and with the
            // check switched off a Gold member's Add Supplementary accepted a
            // complimentary account — and a complimentary member's accepted a
            // Gold one. The card hangs off whatever is in the box and takes
            // *its* tier, so the card issued is not the one the page offered.
            //
            // Coming from a member's page the tier is that member's own, so an
            // unedited number passes and a swapped-in one of another tier is
            // refused. From Add Member it is the tier picked off the menu, as
            // before.
            tier={tier}
            onConfirmed={(found) => {
              setParent(found);
              setStep(3);
            }}
          />
        ) : step === 2 ? (
          <EmployeeGate
            onConfirmed={(number, found) => {
              setEmployeeNumber(number);
              setHr(found);
              setStep(3);
            }}
          />
        ) : (
          <BasicForm
            tier={parent?.membership_tier ?? tier}
            accountType={accountType ?? "primary"}
            parent={parent}
            employeeNumber={employeeNumber}
            hr={hr}
            onCreated={(m) => void navigate(`/members/${m.id}`)}
          />
        )}
      </main>
    </div>
  );
}

/**
 * The parent gate. Debounced as the number is typed, because a clerk types an
 * account number they were told rather than one they picked from a list, and
 * the answer to "is this the right person" is the name beside it.
 *
 * The server owns the verdict: seven things can be wrong with a primary and
 * each has its own sentence. This asks the same function the create will, so
 * what passes here passes there.
 */
function PrimaryGate({
  initial = "",
  locked = false,
  tier,
  onConfirmed,
}: {
  /** A number the caller already knows — the detail page's own account. */
  initial?: string;
  /**
   * Whether that number is the answer rather than a suggestion.
   *
   * Set when the clerk came from a member's own page. The card hangs off that
   * member and takes *its* membership, so the account is not a question this
   * screen is asking — it is the thing the page was opened about. Editable, it
   * was: a Gold member's Add Supplementary accepted a complimentary account
   * typed over the top, and a complimentary member's accepted a Gold one,
   * either way issuing a card that page never offered. Shut, like the two
   * fields under it that are also read off the account.
   */
  locked?: boolean;
  /**
   * The tier this enrolment is for, when a clerk chose one before typing a
   * number — which is Add Member, and only Add Member. A supplementary takes
   * its membership from the primary, so an account of another tier answering
   * this prompt would issue a card nobody picked; the server refuses it and
   * says where to add it instead.
   *
   * Left off when the primary arrived with the clerk, from its own page. There
   * the tier is not a choice that could be wrong — it is whatever that member
   * is, and a complimentary primary issuing a complimentary supplementary is
   * exactly right.
   */
  tier?: Tier;
  onConfirmed: (parent: PrimaryAccount) => void;
}) {
  const t = useTranslation();
  const [typed, setTyped] = useState(initial);
  const [number, setNumber] = useState(initial.trim());
  const [confirming, setConfirming] = useState(false);

  // 400ms: long enough that a ten-digit number is one request, short enough
  // that the name appears while the person is still looking at the field.
  useEffect(() => {
    const timer = setTimeout(() => setNumber(typed.trim()), 400);
    return () => clearTimeout(timer);
  }, [typed]);

  const found = useQuery({
    queryKey: ["members", "primary", number, tier ?? null],
    queryFn: async ({ signal }) =>
      (
        await api.get<PrimaryAccount>(`/members/primary/${encodeURIComponent(number)}`, {
          params: tier ? { tier } : undefined,
          signal,
        })
      ).data,
    enabled: number.length > 0,
    retry: false,
    // Never from the cache. This is not a record, it is a verdict about the
    // primary *now* — how many slots are taken, whether its free card is still
    // to give — and the cache holds answers for thirty seconds by default. Free
    // a card on the member's page and come straight here and the form would
    // open against the answer from before the change.
    staleTime: 0,
    refetchOnMount: "always",
  });
  const parent = found.data ?? null;
  const looking = number.length > 0 && (found.isFetching || typed.trim() !== number);

  return (
    <div className="mx-auto mt-10 max-w-[800px] sm:mt-16">
      <h1 className={cn(STEP_TITLE, "text-center")}>{t("mem.supplementary")}</h1>
      <Card className="mt-8 p-4 md:p-6">
        <h2 className="text-title-3 text-heading">{t("new.primaryMember")}</h2>
        <p className="text-fg-muted text-body mt-1">{t("new.primaryGateBody")}</p>

        <div className="mt-5 grid gap-5">
          {/* No label over it, as legacy has it: the sentence above the card
              is the label, and repeating it in three words over the box would
              be the same instruction twice. The name a screen reader reads is
              that sentence's shorter form. */}
          <Input
            id="parent_account_number"
            // Not focused when there is nothing to type into it; the first
            // thing on the screen a person can act on is Continue.
            autoFocus={!locked}
            readOnly={locked}
            disabled={locked}
            inputMode="numeric"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={t("new.exampleNumber")}
            aria-label={t("new.primaryAccountNumber")}
            aria-invalid={found.isError || undefined}
            aria-describedby="parent_status"
          />

          {/* One live region for all three answers, so a screen reader hears
              the verdict change rather than only sighted users seeing it. */}
          <p id="parent_status" role="status" aria-live="polite" className="text-callout -mt-2">
            {looking ? (
              <span className="text-fg-muted flex items-center gap-2">
                <Spinner /> {t("new.lookingUp")}
              </span>
            ) : found.isError ? (
              <span className="text-danger flex items-start gap-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {errorMessage(found.error, t("new.primaryNotFound"))}
              </span>
            ) : parent ? (
              <span className="text-success flex items-center gap-2">
                <CheckCircle2 className="size-4 shrink-0" aria-hidden />
                {t("new.primaryFound")}
              </span>
            ) : null}
          </p>

          <Field label={req(t("mem.name"))} htmlFor="parent_name">
            <Input
              id="parent_name"
              readOnly
              disabled
              value={parent ? fullName(parent) : ""}
              placeholder={t("new.fromAccount")}
            />
          </Field>
          {/* Two fields, as legacy has them. How long the membership runs is
              not how a clerk tells one member from another, so it waits for
              the confirmation, where the whole account is set out. */}
          <Field label={req(t("mem.dateOfBirth"))} htmlFor="parent_dob">
            <Input
              id="parent_dob"
              readOnly
              disabled
              value={parent?.date_of_birth ?? ""}
              placeholder={t("new.fromAccount")}
            />
          </Field>
          {parent ? (
            <p className="text-fg-muted text-callout">
              {t("new.inheritsFromPrimary")
                .replace("{tier}", t(TIER_LABELS[parent.membership_tier]))
                .replace("{used}", String(parent.supplementaries_in_use))
                // The number this membership is configured with, not a constant:
                // a Business Member carries more than a Gold one.
                .replace("{limit}", String(parent.supplementary_limit))}
            </p>
          ) : null}
        </div>
      </Card>

      <div className="mt-8 flex justify-center">
        <Button disabled={!parent} onClick={() => setConfirming(true)}>
          {/* One word, and it does not change while the lookup runs. The
              button is its own width now, so swapping in "Looking up…" would
              make a centred control grow from both sides mid-wait — and the
              live region above this already says that sentence. */}
          {t("login.continue")}
        </Button>
      </div>

      {/* A card is about to be attached to somebody else's account, off a
          number that was typed. That is worth one look before the form. */}
      <Modal
        open={confirming && !!parent}
        onClose={() => setConfirming(false)}
        title={t("new.verifyDetails")}
        description={t("new.verifyBody")}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t("common.back")}
            </Button>
            <Button onClick={() => parent && onConfirmed(parent)} autoFocus>
              {t("common.confirm")}
            </Button>
          </>
        }
      >
        <dl className="grid gap-3">
          {parent
            ? (
                [
                  [t("new.primaryAccountNumber"), parent.account_number],
                  [t("mem.name"), fullName(parent)],
                  [t("mem.dateOfBirth"), parent.date_of_birth],
                  [
                    t("mem.validUntil"),
                    parent.no_expiry ? t("mem.noExpiry") : (parent.valid_until ?? "—"),
                  ],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4">
                  <dt className="text-fg-muted text-callout">{label}</dt>
                  <dd className="text-fg text-body text-right font-medium">{value}</dd>
                </div>
              ))
            : null}
        </dl>
      </Modal>
    </div>
  );
}

/**
 * The employee gate: one HR number, checked to be nobody else's before the
 * form opens.
 *
 * Checked here as well as in the service because the number is the first thing
 * asked and the last thing anyone wants to be told about after filling in
 * twenty fields. The list's own `employee_number` filter answers it, so this
 * needs no endpoint of its own.
 */
/** What HR knows about the employee, when the lookup answers; seeds the form. */
export interface HrEmployee {
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  email_address: string | null;
}

export function EmployeeGate({
  onConfirmed,
}: {
  onConfirmed: (employeeNumber: string, hr: HrEmployee | null) => void;
}) {
  const t = useTranslation();
  const [number, setNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  /**
   * What HR knows about this number, once it has been looked up and found to
   * be nobody else's.
   *
   * Held rather than carried straight into the form, as legacy has it: the
   * name and email that are about to be filled in are shown first, and the
   * second press takes them. A number is easy to mistype into somebody else's,
   * and the name is what the clerk can tell that from.
   */
  const [found, setFound] = useState<HrEmployee | null>(null);

  const check = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ items: Member[] }>("/members/query", {
        employee_number: number.trim(),
        fields: ["id"],
        size: 1,
      });
      if (data.items.length > 0) return { free: false, hr: null };
      // HR's record fills the name and email in; a number HR does not know
      // still enrols, typed by hand, as the legacy form allowed.
      const hr = await api
        .get<HrEmployee>(`/snr/${encodeURIComponent(number.trim())}`)
        .then((r) => r.data)
        .catch(() => null);
      return { free: true, hr };
    },
    onSuccess: ({ free, hr }) => {
      if (!free) {
        setError(t("new.employeeTaken"));
        return;
      }
      // Nothing of HR's to read, so nothing to stop for: the form opens on a
      // number the clerk will fill in around by hand.
      if (!hr) {
        onConfirmed(number.trim(), null);
        return;
      }
      setFound(hr);
    },
    onError: (err) => setError(errorMessage(err, t("new.employeeCheckFailed"))),
  });

  return (
    <div className="mx-auto mt-10 max-w-[800px] sm:mt-16">
      <h1 className={cn(STEP_TITLE, "text-center")}>{t("new.employeeMember")}</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          // The second press: what was found has been read, so it is taken.
          if (found) {
            onConfirmed(number.trim(), found);
            return;
          }
          check.mutate();
        }}
      >
        <Card className="mt-8 p-4 md:p-6">
          <h2 className="text-title-3 text-heading">{t("new.employeeId")}</h2>
          <p className="text-fg-muted text-body mt-1">{t("new.employeeGateBody")}</p>
          {/* No label over it, as legacy has it: the two lines above the box
              say what goes in it, and a third word over it would not. */}
          <Input
            id="employee_number"
            className="mt-5"
            autoFocus
            required
            value={number}
            onChange={(e) => {
              setNumber(e.target.value);
              setError(null);
              // A different number is a different employee, so what was found
              // for the last one goes rather than sitting under a number it
              // does not belong to.
              setFound(null);
            }}
            placeholder={t("new.exampleNumber")}
            aria-label={t("mem.employeeNumber")}
            aria-invalid={!!error || undefined}
            aria-describedby="employee_status"
          />
          <p
            id="employee_status"
            role="status"
            aria-live="polite"
            className="text-callout mt-2"
          >
            {error ? (
              <span className="text-danger flex items-start gap-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {error}
              </span>
            ) : null}
          </p>
        </Card>

        {/* What HR has on this number, read before it is taken. */}
        {found ? (
          <Card className="mt-4 p-4 md:p-6">
            <h2 className="text-title-3 text-heading">{t("new.employeeInformation")}</h2>
            <p className="text-fg-muted text-body mt-1">{t("new.employeeFoundBody")}</p>
            <div className="mt-4 grid gap-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label={t("mem.firstName")} htmlFor="hr_first_name">
                  <Input id="hr_first_name" readOnly disabled value={found.first_name ?? ""} />
                </Field>
                <Field label={t("mem.middleName")} htmlFor="hr_middle_name">
                  <Input
                    id="hr_middle_name"
                    readOnly
                    disabled
                    value={found.middle_name ?? ""}
                  />
                </Field>
                <Field label={t("mem.lastName")} htmlFor="hr_last_name">
                  <Input id="hr_last_name" readOnly disabled value={found.last_name ?? ""} />
                </Field>
              </div>
              <Field label={t("mem.email")} htmlFor="hr_email">
                <Input id="hr_email" readOnly disabled value={found.email_address ?? ""} />
              </Field>
            </div>
          </Card>
        ) : null}

        <div className="mt-10 flex justify-center">
          <Button
            type="submit"
            disabled={!number.trim()}
            loading={check.isPending}
            loadingLabel={t("new.searching")}
          >
            {t("login.continue")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function WhoToAdd({
  value,
  onChange,
  onContinue,
}: {
  value: AccountType | null;
  onChange: (value: AccountType) => void;
  onContinue: () => void;
}) {
  const t = useTranslation();
  const choices: { key: AccountType; label: string; art: string }[] = [
    { key: "primary", label: t("mem.primary"), art: primaryMemberArt },
    { key: "supplementary", label: t("mem.supplementary"), art: supplementaryMemberArt },
  ];
  return (
    <div className="mx-auto mt-10 max-w-2xl text-center sm:mt-20">
      <h1 className={STEP_TITLE}>{t("new.whoToAdd")}</h1>
      {/* Two across, always. Legacy's compact card is
          `min(214px, calc(50% - 0.5rem))` with a 1rem gap, and the reason is
          plain once you see them stacked: the question is which of the two,
          and an answer you have to scroll to compare is not a choice offered
          side by side. */}
      <div className="mt-6 flex flex-wrap justify-center gap-4 sm:mt-10 sm:gap-6">
        {choices.map((choice) => (
          <button
            key={choice.key}
            type="button"
            onClick={() => onChange(choice.key)}
            aria-pressed={value === choice.key}
            className={cn(
              "bg-surface-raised rounded-card w-[calc(50%-0.5rem)] max-w-[214px] overflow-hidden text-left sm:w-64 sm:max-w-none",
              "transition-[box-shadow,transform] duration-[var(--duration-base)] ease-[var(--ease-out-soft)]",
              value === choice.key
                ? "ring-accent -translate-y-0.5 shadow-[var(--shadow-raised)] ring-2"
                : "shadow-[var(--shadow-lift)] hover:-translate-y-0.5 hover:shadow-[var(--shadow-raised)]",
            )}
          >
            {/* The illustration brings its own tinted panel, so it spans the card edge to edge. */}
            <img src={choice.art} alt="" className="block h-auto w-full" />
            <p className="text-title-3 text-heading py-4 text-center">{choice.label}</p>
          </button>
        ))}
      </div>
      <Button className="mt-7 sm:mt-10" disabled={!value} onClick={onContinue}>
        {t("login.continue")}
      </Button>
    </div>
  );
}

/** The tier's own name, for a sentence about what a supplementary inherits. */
const TIER_LABELS: Record<Tier, TranslationKey> = {
  member: "mem.member",
  affiliates: "mem.affiliates",
  employee: "mem.employee",
  complimentary: "mem.complimentary",
};

const TITLES: Record<Tier, TranslationKey> = {
  member: "new.member",
  affiliates: "new.affiliatesMember",
  employee: "new.employeeMember",
  complimentary: "new.complimentaryMember",
};

function BasicForm({
  tier,
  accountType,
  parent,
  employeeNumber,
  hr = null,
  onCreated,
}: {
  tier: Tier;
  accountType: AccountType;
  /** What the parent gate found, when there was one. */
  parent: PrimaryAccount | null;
  /** What the employee gate cleared, when there was one. */
  employeeNumber: string;
  /** What HR knows about that employee, when the lookup answered. */
  hr?: HrEmployee | null;
  onCreated: (member: Member) => void;
}) {
  const t = useTranslation();
  const byBenefit = BY_BENEFIT.includes(tier);
  const needsAddress = NEEDS_ADDRESS.includes(tier);
  const [v, setV] = useState<Record<string, string>>({
    has_last_name: "true",
    // Not asked at the counter: every enrolment is filed as gold and the type
    // is set afterwards from the member's own record, where it can be changed
    // again. The create still sends it, so the column is never left to guess.
    membership_type: "gold",
    ...(byBenefit ? { acquisition_type: "benefits" } : {}),
    // Whatever a gate settled is given, not asked again.
    ...(parent ? { parent_account_number: parent.account_number } : {}),
    ...(employeeNumber ? { employee_number: employeeNumber } : {}),
    ...(hr?.first_name ? { first_name: hr.first_name } : {}),
    ...(hr?.middle_name ? { middle_name: hr.middle_name } : {}),
    ...(hr?.last_name ? { last_name: hr.last_name } : {}),
    ...(hr?.email_address ? { email: hr.email_address } : {}),
  });
  const set = (name: string, value: string) => setV((d) => ({ ...d, [name]: value }));
  const [error, setError] = useState<string | null>(null);

  // The duplicate rule the create is refused by, asked as the name and
  // birthday are typed rather than after twenty fields — the server's own
  // answer, half a second after the last keystroke.
  const identity = {
    first_name: (v.first_name ?? "").trim(),
    last_name: v.has_last_name === "false" ? null : (v.last_name ?? "").trim() || null,
    date_of_birth: v.date_of_birth ?? "",
  };
  const identityKey = JSON.stringify(identity);
  const [settled, setSettled] = useState(identityKey);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(identityKey), 500);
    return () => clearTimeout(timer);
  }, [identityKey]);
  const duplicate = useQuery({
    queryKey: ["/members/exists", settled],
    queryFn: async ({ signal }) =>
      (
        await api.post<{ results: { exists: boolean; account_number: string | null }[] }>(
          "/members/exists",
          { members: [JSON.parse(settled)] },
          { signal },
        )
      ).data.results[0] ?? { exists: false, account_number: null },
    enabled: identity.first_name.length > 0 && identity.date_of_birth.length > 0,
    staleTime: 30_000,
  });
  const taken = duplicate.data?.exists === true;
  // The rules that read more than one field, judged as they are typed and said
  // under the field that fails them — the submit re-checks every one.
  const tooYoung = !!v.date_of_birth && years(v.date_of_birth) < MINIMUM_AGE;
  /** Whether the member has a last name at all — the switch beside it says. */
  const hasLastName = v.has_last_name !== "false";
  /**
   * A mobile number begun and not finished.
   *
   * The field takes digits only and stops at ten, so the one thing left to go
   * wrong is stopping short — and a number of four digits is not a number the
   * member can be reached on. Said as it is typed rather than on Continue: the
   * clerk is looking at the field, and the card is still in their hand.
   */
  const badMobile = Boolean(v.mobile) && !isValidMobile(v.mobile);

  /**
   * Which fields this form will not go without, for this member.
   *
   * The list is conditional because the form is: an affiliate takes no
   * address, a benefit enrolment has its channel fixed for it, a member with
   * no last name owes none, and the stand-in card is a canvasser's. `missing`
   * and the submit both read this, so a field that says it is owed is a field
   * that actually stops the send.
   */
  const owed: string[] = [
    ...(byBenefit ? [] : ["acquisition_type"]),
    "first_name",
    ...(hasLastName ? ["last_name"] : []),
    "date_of_birth",
    "gender",
    ...(needsAddress ? ["address_street", "city_id", "barangay_id"] : []),
    ...(v.acquisition_type === "canvassing" ? ["temporary_card_number"] : []),
  ];

  /**
   * Whether the form has been sent once.
   *
   * Only then does an empty field read as an omission — before it, it is a
   * field nobody has reached yet, and a form that is scarlet before anybody
   * has typed in it is a form that has told them off for arriving.
   */
  const [tried, setTried] = useState(false);
  /**
   * A required field nobody filled in, once Continue has been pressed.
   *
   * Derived rather than stored, which is what makes the message go away as
   * soon as the field is answered: there is no second place holding "this was
   * wrong once" to be cleared by hand and forgotten about.
   */
  const missing = (name: string) => tried && owed.includes(name) && !v[name];
  const missingBirthday = missing("date_of_birth");
  /**
   * Neither way of reaching the member, once Continue has been pressed.
   *
   * Said under both fields rather than one, because either of them answers it
   * and neither is the wrong one to fill in — the note under the pair has
   * always stated the rule; this is the same rule said where it failed.
   */
  const noContact = tried && !v.mobile && !v.email;
  /**
   * An agreement not given, once Continue has been pressed.
   *
   * A checkbox holds "true" or nothing, so it cannot use `missing` — and the
   * two on this form are not fields somebody forgot to fill in but consents
   * the enrolment cannot proceed without, which is why each says so itself
   * rather than leaving the banner to speak for both.
   */
  const missingAgreement = (name: string) => tried && v[name] !== "true";
  /**
   * Whether the form is currently saying something is wrong with itself.
   *
   * The button reads this rather than its own list, so Continue is shut for
   * exactly as long as there is a message on screen — pressing it again while
   * the page is scarlet did nothing but set the same messages a second time,
   * which looks like a button that does not work.
   *
   * Every term here is false until Continue has been pressed once, so the
   * button starts live: a form that arrives with its own Continue greyed out
   * is a form that has locked somebody out before they typed anything.
   */
  const showingErrors =
    owed.some((field) => missing(field)) ||
    noContact ||
    badMobile ||
    missingAgreement("agree_term") ||
    missingAgreement("agree_personal_info");
  /**
   * The banner over Continue, while anything under it is still marked.
   *
   * Derived, not stored: it says "the fields marked above", so it has to go
   * when the marks do. As state it outlived them — every field answered, every
   * message gone, and a red panel still sitting over a form that was ready to
   * send.
   */
  const incomplete = tried && showingErrors;
  /**
   * The identity already on file, said under each of the three fields that
   * make one up.
   *
   * A name and a birthday are one fact between them, and the answer "somebody
   * else is already this person" belongs to all three — under the birth date
   * alone it read as a complaint about the date, which is the one of the three
   * least likely to be the mistake.
   */
  const alreadyAMember = taken ? t("new.memberExists") : undefined;
  const needsMobile =
    !v.mobile && (v.phone_consent === "true" || v.only_important_updates === "true");
  const needsEmail = !v.email && v.email_consent === "true";
  const [consent, setConsent] = useState<ConsentText | null>(null);

  // A walk-in was seen and photographed at the counter, so both pictures are
  // owed. Said under the card that is missing as soon as walk-in is chosen,
  // rather than saved up for the submit.
  const walkIn = v.acquisition_type === "walk_in";
  const needsIdCard = walkIn && !byBenefit && !v.id_card_image;
  const needsPhoto = walkIn && !v.profile_image;

  // ── The ID card, read into the fields above ──
  const [cardError, setCardError] = useState<string | null>(null);
  const scan = useMutation({
    mutationFn: async (picture: string) => {
      const body = new FormData();
      body.append("file", dataUrlToBlob(picture), "id-card.jpg");
      return (await api.post<IdCard>("/ocr/extract-from-document", body)).data;
    },
    onSuccess: (card) => setV((d) => ({ ...d, ...fieldsFromCard(card) })),
    onError: (err) => setCardError(cardProblem(err, t)),
  });

  const takeIdCard = async (file: File) => {
    setCardError(null);
    scan.reset();
    let picture: string;
    try {
      picture = await readPicture(file);
    } catch {
      setCardError(t("new.pictureUnreadable"));
      return;
    }
    // The old card's fields go before the new card's arrive, so a field the
    // second card does not show cannot be left holding the first card's answer.
    setV((d) => ({ ...d, ...blankCardFields(), id_card_image: picture }));
    scan.mutate(picture);
  };

  const removeIdCard = () => {
    setCardError(null);
    scan.reset();
    setV((d) => ({ ...d, ...blankCardFields(), id_card_image: "" }));
  };

  const [photoError, setPhotoError] = useState<string | null>(null);
  const takePhoto = async (file: File) => {
    setPhotoError(null);
    try {
      // Kept square-ish and small: this one is the member's avatar, shown at
      // thirty-six pixels far more often than it is shown at full size.
      set("profile_image", await readPicture(file, 1000));
      // What it was called where it came from. The picture is stored under a
      // key of our own making, so this is the only thing left that says which
      // file — or which capture — the photograph on the record came from.
      set("profile_image_name", file.name);
    } catch {
      setPhotoError(t("new.pictureUnreadable"));
    }
  };

  const create = useMutation({
    mutationFn: async () => {
      const hasLast = v.has_last_name !== "false";
      const body = {
        membership_tier: tier,
        membership_type: v.membership_type || "gold",
        account_type: accountType,
        employee_number: v.employee_number || null,
        company_name: v.company_name || null,
        // Not a column: it is who the complimentary note the create files says
        // signed the membership off. See `docs/member-create-flow.md` §3.
        approved_by_id: v.approved_by_id ? Number(v.approved_by_id) : null,
        prefix: v.prefix || null,
        suffix: v.suffix || null,
        first_name: v.first_name,
        middle_name: v.middle_name || null,
        last_name: hasLast ? v.last_name || null : null,
        date_of_birth: v.date_of_birth || null,
        gender: v.gender || null,
        acquisition_type: v.acquisition_type || null,
        mobile_number: v.mobile ? `+63${v.mobile}` : null,
        email: v.email || null,
        unit_number: v.unit_number || null,
        building_name: v.building_name || null,
        address_street: v.address_street || null,
        city_id: v.city_id ? Number(v.city_id) : null,
        barangay_id: v.barangay_id ? Number(v.barangay_id) : null,
        parent_account_number: v.parent_account_number || null,
        // Only what the canvassing field was actually typed into: every other
        // channel enrols without a stand-in card, and one is never invented.
        temporary_card_number:
          v.acquisition_type === "canvassing" ? v.temporary_card_number || null : null,
        email_consent: v.email_consent === "true",
        phone_consent: v.phone_consent === "true",
        only_important_updates: v.only_important_updates === "true",
        // The two agreements are still required to proceed, below; nothing
        // stores the answer, so nothing is sent.
        id_card_image: v.id_card_image || null,
        profile_image: v.profile_image || null,
        // Only ever true for the supplementary the gate offered it on.
        is_free_supplementary: v.is_free_supplementary === "true",
        profile_image_name: (v.profile_image && v.profile_image_name) || null,
      };
      return (await api.post<Member>("/members", body)).data;
    },
    onSuccess: (member) => {
      toast.success(
        t("new.enrolled")
          .replace("{name}", fullName(member))
          .replace("{number}", member.account_number),
      );
      onCreated(member);
    },
    onError: (err) => setError(errorMessage(err, t("new.enrolFailed"))),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setTried(true);
    if (taken) {
      setError(t("new.duplicateMember"));
      return;
    }
    /**
     * The one required field the browser does not check.
     *
     * Every other field on this form is an `<input required>`, so the submit
     * never runs while one of them is empty. The birth date is the app's own
     * calendar rather than `type="date"`, which puts it outside that net — and
     * without this, a blank one fell through to the age rule below and was
     * refused as "too young", which is not what is wrong with it.
     */
    /**
     * Every required field, judged together.
     *
     * Each is already saying so under itself — `missing` reads the same values
     * this does — so the banner names the count rather than repeating the
     * first one it found, and nothing is sent while any of them is short.
     */
    if (
      owed.some((field) => !v[field]) ||
      (!v.mobile && !v.email) ||
      badMobile ||
      v.agree_term !== "true" ||
      v.agree_personal_info !== "true"
    ) {
      // Nothing stored. The banner is read off the same values the fields
      // are, below, so it clears itself the moment the last one is answered —
      // held in state it sat there over a form with nothing wrong with it,
      // and only the next press of Continue could take it away.
      return;
    }
    // The browser has already checked every `required` field; what is left is
    // the rules that read more than one of them.
    if (years(v.date_of_birth) < MINIMUM_AGE) {
      setError(t("new.tooYoung"));
      return;
    }
    // Consent to be written to makes that way of writing required; with no
    // consent at all, one of the two is still needed to reach the member.
    if (!v.mobile && (v.phone_consent === "true" || v.only_important_updates === "true")) {
      setError(t("new.mobileRequired"));
      return;
    }
    if (!v.email && v.email_consent === "true") {
      setError(t("new.emailRequired"));
      return;
    }
    if (!v.mobile && !v.email) {
      setError(t("new.contactRequired"));
      return;
    }
    if (v.agree_term !== "true" || v.agree_personal_info !== "true") {
      setError(t("new.consentsRequired"));
      return;
    }
    // Somebody at the counter was seen and photographed; a canvasser hands
    // over a stand-in card there and then. The service checks both again.
    if (needsIdCard || needsPhoto) {
      setError(t("new.walkInNeedsPictures"));
      return;
    }
    if (v.acquisition_type === "canvassing") {
      if (!v.temporary_card_number) {
        setError(t("new.canvassingNeedsCard"));
        return;
      }
      // Eleven digits, the length the cards are printed at — the same rule the
      // card edit and the API hold the number to.
      const bad = temporaryCardError(v.temporary_card_number);
      if (bad) {
        setError(t(bad));
        return;
      }
    }
    create.mutate();
  };

  return (
    /**
     * The form says what is wrong, not the browser.
     *
     * Every required field carried `required`, and the browser refused the
     * submit before `submit` ran — which meant one bubble on one field, in the
     * browser's own wording and the browser's own language, and gone the
     * moment anything else was clicked. It also meant `tried` was never set,
     * so none of the messages under the fields could ever appear. `noValidate`
     * hands the judging to `submit`, which marks every field that is short at
     * once; the attributes stay on the fields for what they still say to a
     * screen reader.
     */
    /**
     * `autoComplete="off"`, because this form is not the person filling it in.
     *
     * A clerk enrols a member from the card in front of them, on a machine
     * that enrols the next one an hour later. Chrome reads a street, a city
     * and a name on one form and offers to save the lot to *the clerk's own*
     * Google account on submit — a stranger's address filed under the person
     * at the counter, and their saved addresses offered back as suggestions
     * over the next member's details.
     */
    <form noValidate autoComplete="off" onSubmit={submit} className="mx-auto mt-12 max-w-2xl">
      <h1 className={cn(STEP_TITLE, "text-center")}>
        {accountType === "primary" ? t("mem.primary") : t("mem.supplementary")}{" "}
        {t(TITLES[tier])}
      </h1>

      <div
        className={cn("mt-10 mb-8 grid gap-4", byBenefit ? "grid-cols-1" : "md:grid-cols-2")}
      >
        {/* A benefit enrolment is vouched for by the club, not by an ID. */}
        {byBenefit ? null : (
          <Upload
            title={t("new.idCard")}
            action={t("new.scanId")}
            sheetTitle={t("new.uploadIdCard")}
            dropMessage={t("new.dropIdCard")}
            value={v.id_card_image}
            busy={scan.isPending}
            error={cardError ?? (needsIdCard ? t("new.idCardForWalkIn") : null)}
            onPick={(file) => void takeIdCard(file)}
            onRemove={removeIdCard}
          />
        )}
        <Upload
          title={t("new.photo")}
          action={t("new.capturePhoto")}
          sheetTitle={t("new.uploadPhoto")}
          dropMessage={t("new.dropPhoto")}
          guide
          value={v.profile_image}
          error={photoError ?? (needsPhoto ? t("new.photoForWalkIn") : null)}
          onPick={(file) => void takePhoto(file)}
          onRemove={() => {
            set("profile_image", "");
            set("profile_image_name", "");
          }}
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-title-3 text-heading">{t("md.basicInformation")}</h2>
            <p className="text-fg-muted text-subhead mt-1">{t("new.autoFill")}</p>
          </div>
          <div className="flex w-full flex-wrap gap-4 sm:w-auto">
            <div className="w-full sm:w-64">
              {/* A Business Primary carries one supplementary card that costs
                  nothing, and this is the card being written — so the switch
                  sits with the channel rather than in a card of its own. It is
                  here only while that primary still has its free card to give;
                  the server refuses a second one either way. */}
              {parent?.free_supplementary_offered ? (
                <label className="text-fg text-callout mb-3 flex items-center justify-end gap-2.5">
                  {t("new.freeSupplementary")}
                  <Checkbox
                    checked={v.is_free_supplementary === "true"}
                    onChange={(event) =>
                      set("is_free_supplementary", event.target.checked ? "true" : "false")
                    }
                  />
                </label>
              ) : null}
              {byBenefit ? (
                // Fixed, not chosen: these three tiers are enrolled as a
                // benefit and nothing else, so a select would be one option.
                <Field label={t("mem.acquisitionType")} htmlFor="acquisition_type">
                  <Input id="acquisition_type" value={t("mem.benefits")} disabled readOnly />
                </Field>
              ) : (
                <Choice
                  name="acquisition_type"
                  label={req(t("mem.acquisitionType"))}
                  placeholder={t("new.selectAcquisition")}
                  options={ADD_MEMBER_ACQUISITION(t)}
                  required
                  v={v}
                  // The stand-in card is a canvasser's, and the field is only
                  // on the form while canvassing is the answer. A number typed
                  // before the channel was changed goes with the field: kept,
                  // it would be saved to a member nobody handed a card to.
                  set={(name, value) =>
                    setV((d) => ({
                      ...d,
                      [name]: value,
                      ...(value === "canvassing" ? {} : { temporary_card_number: "" }),
                    }))
                  }
                  error={
                    missing("acquisition_type") ? t("new.acquisitionTypeRequired") : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>

        {/* `items-start`, so a message under one field does not stretch the
            one beside it. The grid stretches its items to the tallest in the
            row by default, and a `Field` is itself a grid — so the row that
            grew to fit "Gender is required" handed the extra height to the
            mobile box next to it, which then stood taller than its own +63 and
            lower than every other field on the form. */}
        <div className="mt-6 grid items-start gap-x-8 gap-y-5 md:grid-cols-2">
          {accountType === "supplementary" ? (
            <div className="md:col-span-2">
              <Field label={t("new.primaryAccountNumber")} htmlFor="parent_account_number">
                <Input
                  id="parent_account_number"
                  readOnly
                  disabled
                  value={v.parent_account_number ?? ""}
                />
              </Field>
              <p className="text-fg-muted text-callout mt-2">{t("new.inheritedFromPrimary")}</p>
            </div>
          ) : null}
          {/* The one field the tier adds. Every tier has at most one, so this
              is a switch rather than a section per tier. */}
          {tier === "employee" ? (
            <Field label={t("mem.employeeNumber")} htmlFor="employee_number">
              {/* Settled by the gate, and checked to be nobody else's there. */}
              <Input id="employee_number" readOnly disabled value={v.employee_number ?? ""} />
            </Field>
          ) : null}
          {tier === "affiliates" ? (
            <Field label={t("mem.companyName")} htmlFor="company_name">
              <Input
                id="company_name"
                value={v.company_name ?? ""}
                onChange={(e) => set("company_name", e.target.value)}
                placeholder={t("new.enterCompanyName")}
              />
            </Field>
          ) : null}
          {tier === "complimentary" ? (
            <Field label={req(t("new.approvedBy"))} htmlFor="approved_by_id">
              {/* A name from a configured list, not free text: the answer is
                  filed as the member's complimentary note, which holds it by
                  id. Legacy called the same list "S&R family owners". */}
              <FkSelect
                id="approved_by_id"
                required
                endpoint="/reasons/compact"
                params={{ kind: "complimentary_approved_by" }}
                value={v.approved_by_id ?? ""}
                onChange={(next) => set("approved_by_id", next)}
                placeholder={t("new.selectApprovedBy")}
              />
            </Field>
          ) : null}
          <Choice
            name="prefix"
            label={t("mem.prefix")}
            placeholder={t("new.selectPrefix")}
            options={PREFIX(t)}
            v={v}
            set={set}
          />
          <Choice
            name="suffix"
            label={t("mem.suffix")}
            placeholder={t("new.selectSuffix")}
            options={SUFFIX()}
            v={v}
            set={set}
          />
          <Field label={req(t("mem.firstName"))} htmlFor="first_name">
            <NameInput
              id="first_name"
              required
              value={v.first_name ?? ""}
              onChange={(next) => set("first_name", next)}
              placeholder={t("new.enterFirstName")}
              aria-invalid={missing("first_name") || !!alreadyAMember || undefined}
            />
            {missing("first_name") ? <FieldError text={t("new.firstNameRequired")} /> : null}
            {!missing("first_name") && alreadyAMember ? (
              <FieldError text={alreadyAMember} />
            ) : null}
          </Field>
          <Field label={t("mem.middleName")} htmlFor="middle_name">
            <NameInput
              id="middle_name"
              value={v.middle_name ?? ""}
              onChange={(next) => set("middle_name", next)}
              placeholder={t("new.enterMiddleName")}
            />
          </Field>
          <Field
            /* The asterisk is the switch's answer, not a constant. A field
               greyed out because the member has no last name is not a field
               somebody has failed to fill in. */
            label={hasLastName ? req(t("mem.lastName")) : t("mem.lastName")}
            htmlFor="last_name"
            className="relative"
          >
            <NameInput
              id="last_name"
              required={hasLastName}
              disabled={!hasLastName}
              value={v.last_name ?? ""}
              onChange={(next) => set("last_name", next)}
              // A box that cannot be typed in should not be inviting it. What
              // it says instead is what the switch just said about the member.
              placeholder={hasLastName ? t("new.enterLastName") : t("md.na")}
              aria-invalid={
                (hasLastName && (missing("last_name") || !!alreadyAMember)) || undefined
              }
            />
            {/* Nothing is owed by a member who has no last name: the switch
                beside the label has already answered for this field. */}
            {hasLastName && missing("last_name") ? (
              <FieldError text={t("new.lastNameRequired")} />
            ) : null}
            {hasLastName && !missing("last_name") && alreadyAMember ? (
              <FieldError text={alreadyAMember} />
            ) : null}
            {/*
              Beside the label on screen, after the box in the document.

              Written into the label, the switch sat between Middle Name and
              Last Name in the tab order: Tab out of the middle name and the
              next stop was the switch rather than the field it governs, which
              is not the order anybody fills a form in. Out of the label it is
              also out of the way — a press on the words no longer reaches the
              last-name box, and the box keeps the label to itself.
            */}
            <span className="text-fg-muted text-footnote absolute top-0 right-0 flex h-4 items-center gap-2 font-normal">
              {t("new.toggleNoLastName")}
              <Toggle
                size="sm"
                // Blue, as this switch has always been drawn, rather than the
                // green the app gives a setting that is simply on: this one is
                // a fact about the member, not an approval.
                tone="accent"
                aria-label={t("new.toggleNoLastName")}
                // On is "they have none", the way the walk-in form reads: the
                // switch is the exception, so it starts off.
                checked={!hasLastName}
                onChange={(e) =>
                  setV((d) => ({
                    ...d,
                    has_last_name: e.target.checked ? "false" : "true",
                    // What they do not have, they do not keep half-typed: a
                    // name already entered goes rather than sitting greyed out
                    // behind the switch.
                    ...(e.target.checked ? { last_name: "" } : {}),
                  }))
                }
              />
            </span>
          </Field>
          <Field label={req(t("mem.dateOfBirth"))} htmlFor="date_of_birth">
            {/* The app's calendar, not the operating system's. `type="date"`
                draws whatever the platform draws — dd/mm/yyyy on a machine set
                to it, which is the other way round from every date this system
                writes — and offers no keyboard path on some of them. This is
                the same picker the members list and the card dialog use:
                MM/DD/YYYY, and the days it will not take greyed out. */}
            <DatePicker
              id="date_of_birth"
              value={v.date_of_birth ?? ""}
              onChange={(next) => set("date_of_birth", next)}
              // A birth date is not in the future, and not two centuries back
              // — the bounds the API enforces, so a day it would refuse cannot
              // be reached. Being under age is a sentence under the field
              // rather than a bound: "no such day" is the wrong way to tell
              // somebody the member is too young to enrol.
              min={birthDateMin()}
              max={today()}
              describedBy="identity_status"
            />
            <p
              id="identity_status"
              role="status"
              aria-live="polite"
              className="text-callout mt-2"
            >
              {missingBirthday ? (
                <span className="text-danger flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {t("new.dobRequired")}
                </span>
              ) : alreadyAMember ? (
                <span className="text-danger flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {/* The same three words the two name fields carry, and the
                      account number after them: this is the one of the three
                      places with room to say which member it already is. */}
                  {alreadyAMember}
                  {duplicate.data?.account_number ? ` (${duplicate.data.account_number})` : ""}
                </span>
              ) : tooYoung ? (
                <span className="text-danger flex items-start gap-2">
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  {t("new.tooYoung")}
                </span>
              ) : null}
            </p>
          </Field>
          <Choice
            name="gender"
            label={req(t("mem.gender"))}
            placeholder={t("new.selectGender")}
            options={GENDER(t)}
            required
            error={missing("gender") ? t("new.genderRequired") : undefined}
            v={v}
            set={set}
          />
          <Field label={t("mem.mobileNumber")} htmlFor="mobile">
            <div className="flex">
              <span className="rounded-field border-line bg-surface-sunken text-fg text-body inline-flex items-center rounded-r-none border border-r-0 px-3.5">
                +63
              </span>
              <Input
                id="mobile"
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                className="rounded-l-none"
                value={v.mobile ?? ""}
                onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                placeholder="XXXXXXXXXX"
                aria-invalid={badMobile || needsMobile || noContact || undefined}
              />
            </div>
            {badMobile ? (
              <FieldError text={t("new.mobileDigits")} />
            ) : needsMobile ? (
              <FieldError text={t("new.mobileRequired")} />
            ) : noContact ? (
              <FieldError text={t("new.eitherContact")} />
            ) : null}
          </Field>
          <div className="md:col-span-2">
            <Field label={t("mem.email")} htmlFor="email">
              <Input
                id="email"
                type="email"
                maxLength={254}
                value={v.email ?? ""}
                onChange={(e) => set("email", e.target.value)}
                placeholder={t("new.enterEmail")}
                aria-invalid={needsEmail || noContact || undefined}
              />
              {needsEmail ? (
                <FieldError text={t("new.emailRequired")} />
              ) : noContact ? (
                <FieldError text={t("new.eitherContact")} />
              ) : null}
            </Field>
            <p className="text-fg-muted text-callout mt-2">
              <span className="text-danger">*</span> {t("new.contactNote")}
            </p>
          </div>
          {/* An affiliate and a complimentary member are enrolled on the
              strength of who vouched for them, so no address is taken. */}
          {needsAddress ? (
            <>
              <Field label={t("mem.unitNumber")} htmlFor="unit_number">
                <Input
                  id="unit_number"
                  value={v.unit_number ?? ""}
                  onChange={(e) => set("unit_number", e.target.value)}
                  placeholder={t("new.enterUnitNumber")}
                />
              </Field>
              <Field label={t("mem.buildingName")} htmlFor="building_name">
                <Input
                  id="building_name"
                  value={v.building_name ?? ""}
                  onChange={(e) => set("building_name", e.target.value)}
                  placeholder={t("new.enterBuildingName")}
                />
              </Field>
              <Field label={req(t("mem.addressStreet"))} htmlFor="address_street">
                <Input
                  id="address_street"
                  required
                  value={v.address_street ?? ""}
                  onChange={(e) => set("address_street", e.target.value)}
                  placeholder={t("new.enterAddress")}
                  aria-invalid={missing("address_street") || undefined}
                />
                {missing("address_street") ? (
                  <FieldError text={t("new.addressRequired")} />
                ) : null}
              </Field>
              <Field label={req(t("mem.city"))} htmlFor="city_id">
                <FkSelect
                  id="city_id"
                  required
                  endpoint="/cities/compact"
                  value={v.city_id ?? ""}
                  onChange={(next) => {
                    set("city_id", next);
                    set("barangay_id", "");
                  }}
                  placeholder={t("new.selectCity")}
                  invalid={missing("city_id")}
                />
                {missing("city_id") ? <FieldError text={t("new.cityRequired")} /> : null}
              </Field>
              <Field label={req(t("mem.barangay"))} htmlFor="barangay_id">
                <FkSelect
                  id="barangay_id"
                  required
                  endpoint="/barangays/compact"
                  params={{ city_id: v.city_id }}
                  disabled={!v.city_id}
                  value={v.barangay_id ?? ""}
                  onChange={(next) => set("barangay_id", next)}
                  placeholder={v.city_id ? t("new.selectBarangay") : t("new.selectCityFirst")}
                  // Only once a city is chosen. A barangay cannot be picked
                  // before one, so calling it missing is telling somebody off
                  // for a field the form has not let them reach.
                  invalid={Boolean(v.city_id) && missing("barangay_id")}
                />
                {v.city_id && missing("barangay_id") ? (
                  <FieldError text={t("new.barangayRequired")} />
                ) : null}
              </Field>
            </>
          ) : null}
          {/*
            A canvasser hands over a stand-in card at the door; the real one is
            printed later. The field appears only when it is owed.

            Last in the section, after the address — and outside it, because
            the stand-in card is owed by how the member was signed up, not by
            which tier they are: an affiliate takes no address and can still be
            enrolled at the door.
          */}
          {v.acquisition_type === "canvassing" ? (
            <Field label={req(t("mem.temporaryCardNumber"))} htmlFor="temporary_card_number">
              <Input
                id="temporary_card_number"
                required
                // A field labelled with a card number is one the browser offers
                // a saved payment card for. Nothing but the number on the card
                // in the canvasser's hand belongs here.
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]{11}"
                maxLength={TEMPORARY_CARD_DIGITS}
                title={t("new.temporaryCardDigits")}
                value={v.temporary_card_number ?? ""}
                onChange={(e) =>
                  set("temporary_card_number", sanitizeTemporaryCard(e.target.value))
                }
                placeholder={t("new.enterTemporaryCard")}
                aria-invalid={missing("temporary_card_number") || undefined}
              />
              {missing("temporary_card_number") ? (
                <FieldError text={t("new.temporaryCardRequired")} />
              ) : null}
            </Field>
          ) : null}
        </div>
      </Card>

      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("new.receiveUpdates")}</h2>
        <p className="text-fg-muted text-callout mt-2 max-w-3xl">{t("new.receiveBody")}</p>
        <div className="mt-4 flex flex-wrap gap-6">
          <Check name="email_consent" label={t("mem.email")} v={v} set={set} />
          <Check name="phone_consent" label={t("mem.mobileNumber")} v={v} set={set} />
        </div>
        <div className="mt-4">
          <Check
            name="only_important_updates"
            label={t("mem.onlyImportantUpdates")}
            v={v}
            set={set}
          />
        </div>
      </Card>

      <Card className="mt-8">
        <h2 className="text-title-3 text-heading">{t("new.privacyConsent")}</h2>
        <div className="mt-4 grid gap-4">
          <Check
            name="agree_term"
            error={missingAgreement("agree_term") ? t("new.mustAgreeTerms") : undefined}
            v={v}
            set={set}
            label={
              <>
                {t("new.agreeTerms")}{" "}
                <TextLink onClick={() => setConsent("terms")}>
                  {t("new.termsAndConditions")}
                </TextLink>{" "}
                {t("new.and")}{" "}
                <TextLink onClick={() => setConsent("privacy")}>
                  {t("new.privacyPolicy")}
                </TextLink>
                .
              </>
            }
          />
          <Check
            name="agree_personal_info"
            error={
              missingAgreement("agree_personal_info") ? t("new.mustAgreePersonal") : undefined
            }
            v={v}
            set={set}
            label={
              <>
                {t("new.agreePersonal")}{" "}
                <TextLink onClick={() => setConsent("reasons")}>
                  {t("new.statedReasons")}
                </TextLink>
                .
              </>
            }
          />
        </div>
      </Card>

      <ConsentTextModal text={consent} onClose={() => setConsent(null)} />

      {incomplete || error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout mx-auto mt-6 flex max-w-md items-start gap-2 rounded-xl px-3.5 py-3"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {/* What is marked above comes first: a save that failed for some
              other reason is worth reading, but not over a form that has not
              been filled in yet. */}
          <span>{incomplete ? t("new.fieldsIncomplete") : error}</span>
        </p>
      ) : null}
      <div className="mt-8 flex justify-center pb-10">
        <Button
          type="submit"
          // Held back for the same four things legacy held it back for: a save
          // already in flight, a rule the form is already showing broken, a
          // duplicate the server would refuse, and — ours — a card still being
          // read, which is about to rewrite half the fields above.
          disabled={
            taken ||
            tooYoung ||
            badMobile ||
            needsMobile ||
            needsEmail ||
            showingErrors ||
            scan.isPending
          }
          loading={create.isPending || scan.isPending}
          loadingLabel={scan.isPending ? t("new.readingCard") : t("new.saving")}
        >
          {t("login.continue")}
        </Button>
      </div>
    </form>
  );
}

function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section
      className={cn(
        // 1rem of padding on a phone rather than 28px, which is legacy's
        // `&__form-card`: at 340px wide the padding was a sixth of the card.
        "bg-surface-raised rounded-card p-4 shadow-[var(--shadow-lift)] md:p-7",
        className,
      )}
    >
      {children}
    </section>
  );
}

/**
 * The delete that sits on a picture, in the middle of it.
 *
 * Hidden until the pointer is on the picture, as legacy had it: the picture is
 * the point, and a delete standing on it at all times reads as part of it. It
 * watches two named groups because there are two pictures — the card's and the
 * sheet's — and a picture should answer only to a pointer on itself.
 */
const OVER_PICTURE = cn(
  "absolute top-1/2 left-1/2 grid size-9 -translate-x-1/2 -translate-y-1/2",
  "place-items-center rounded-full bg-black/55 text-white opacity-0",
  "transition-opacity duration-[var(--duration-base)] hover:bg-black/75",
  "group-hover/preview:opacity-100 group-hover/staged:opacity-100 focus-visible:opacity-100",
);

/**
 * A press on a control standing on something that is itself pressable.
 *
 * The card and the sheet's panel both open a picker wherever they are clicked,
 * which is what makes a target out of them; everything sitting on that target
 * has to say it is not part of it.
 */
const stopAnd = (event: React.MouseEvent, then: () => void) => {
  event.stopPropagation();
  then();
};

/**
 * How tall a card's picture area is, in both of its states.
 *
 * The height of the preview that goes in it, so the card is the size it was
 * before a picture arrived and nothing below it moves when one is dropped in.
 * Legacy let the card grow to the picture instead, which walks the whole form
 * down the screen twice: once for the card, once for the photograph. The
 * placeholder needs only 118 of these pixels — the camera link, then the
 * dropzone — and is centred in the rest.
 */
const PICTURE_AREA = "h-40"; /* 160px, the preview's own height */

/**
 * The preview, at the size legacy gives it: 234x160, a card held landscape,
 * with the corners of everything else on the page. `max-w-full` is for the
 * narrow end of the two-column row, where the card is not 234 pixels wide to
 * give; the height does not move with it, so neither does the card.
 */
const PICTURE = "h-full w-[234px] max-w-full rounded-xl object-cover";

/**
 * One upload card: the ID card, or the photograph.
 *
 * The whole card is the file picker — legacy made the card itself clickable,
 * and a 234-pixel dropzone with a live preview in it is a bigger target than
 * any button on it. The two things that are not the picker say so by
 * cancelling the label: the camera link above the dropzone, and the delete
 * over the preview.
 */
export function Upload({
  title,
  action,
  sheetTitle,
  dropMessage,
  guide,
  value,
  busy = false,
  error,
  onPick,
  onRemove,
}: {
  title: string;
  /** The link that reaches the camera: "Scan ID", "Capture photo". */
  action: string;
  /** The sheet's heading: "Upload ID Card", "Upload Photo". */
  sheetTitle: string;
  /** The line inside the sheet's dropzone, naming what it takes. */
  dropMessage: string;
  /**
   * Whether this is the photograph rather than the card. The photograph is of
   * a person who has to be looking at the lens in good light, so the rules
   * come first and the camera second; a card is a card.
   */
  guide?: boolean;
  value: string | undefined;
  /** The picture is on screen but something is still being done with it. */
  busy?: boolean;
  /** Why this picture cannot be used, said under the card. */
  error?: string | null;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const t = useTranslation();
  const file = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  /**
   * Which sheet is up: none, the attachment sheet, the photograph's rules, or
   * the lens.
   */
  const [stage, setStage] = useState<"none" | "sheet" | "guide" | "camera">("none");
  /**
   * The picture chosen in the sheet and not yet taken.
   *
   * Legacy puts a sheet between the file picker and the card — a picture is
   * looked at and then said Upload to, rather than landing on the record the
   * instant a file is double-clicked. The ID card is read by OCR on the way
   * in, so the difference is a scan of the right card rather than of whatever
   * the pointer happened to be on.
   */
  const [staged, setStaged] = useState<File | null>(null);
  /**
   * Whether the camera was reached from the sheet, and so should hand its
   * capture back to it rather than straight to the card.
   */
  const [viaSheet, setViaSheet] = useState(false);

  const take = (picked: File | undefined) => {
    if (picked && picked.type.startsWith("image/")) setStaged(picked);
  };
  const browse = () => file.current?.click();
  /** Open the sheet, with a dragged picture already in it if there was one. */
  const openSheet = (dropped?: File) => {
    setStaged(dropped ?? null);
    setStage("sheet");
  };
  const closeSheet = () => {
    setStaged(null);
    setStage("none");
  };
  const toCamera = (fromSheet: boolean) => {
    setViaSheet(fromSheet);
    setStage(guide ? "guide" : "camera");
  };

  return (
    <div>
      {/* Clickable, not focusable: everything it does is reachable from the
          real controls inside it, so making the card itself a tab stop would
          put a third stop in front of them that announces the same thing. */}
      <div
        onClick={() => openSheet()}
        className={cn(
          "border-line bg-surface-raised flex cursor-pointer flex-col items-start rounded-xl border px-6 py-4",
          over && "border-accent bg-accent-soft/30",
          error && "border-danger",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          // Dropped on the card, looked at in the sheet: the same two steps a
          // picture takes when it is picked rather than dragged.
          const dropped = e.dataTransfer.files[0];
          if (dropped?.type.startsWith("image/")) openSheet(dropped);
        }}
      >
        <span className="text-body text-heading mb-3 font-semibold">{title}</span>
        <div className={cn("flex w-full flex-col items-center justify-center", PICTURE_AREA)}>
          {value ? (
            <span className="group/preview relative inline-block h-full">
              {/* Filled to the box rather than fitted into it, as legacy has
                  it: a picture that keeps its own shape leaves the two cards
                  with different pictures in them different sizes. The ground
                  under it is the page's rather than legacy's flat grey, which
                  would be a light patch on a dark screen. */}
              <img src={value} alt="" className={cn("bg-surface-sunken", PICTURE)} />
              {busy ? (
                <span className="absolute inset-0 grid place-items-center rounded-xl bg-black/45">
                  <Spinner className="text-white" />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => stopAnd(e, onRemove)}
                  className={OVER_PICTURE}
                >
                  <Trash2 className="size-4" aria-hidden />
                  <span className="sr-only">{t("new.removePicture")}</span>
                </button>
              )}
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => stopAnd(e, () => toCamera(false))}
                className="text-accent text-body font-semibold"
              >
                {action}
              </button>
              <button
                type="button"
                onClick={(e) => stopAnd(e, () => openSheet())}
                className="flex flex-col items-center gap-1 p-4 text-center"
              >
                <CloudUpload className="text-fg-subtle size-6" aria-hidden />
                <span className="text-fg-muted text-callout">
                  <span className="text-accent font-semibold underline">
                    {t("new.clickToUpload")}
                  </span>{" "}
                  {t("new.orDrag")}
                </span>
                <span className="text-fg-subtle text-subhead">{t("new.pngOrJpg")}</span>
              </button>
            </>
          )}
        </div>
      </div>
      {error ? (
        <p role="status" className="text-danger text-subhead mt-1">
          {error}
        </p>
      ) : null}

      {/* The attachment sheet: a picture is chosen, looked at, and only then
          said Upload to. Legacy puts this between every file picker and the
          record it writes to. */}
      {stage === "sheet" ? (
        <Modal
          open
          onClose={closeSheet}
          title={sheetTitle}
          size="md"
          footerDivider={false}
          footer={
            <div className="flex w-full justify-end gap-3">
              <Button variant="secondary" onClick={closeSheet}>
                {t("common.cancel")}
              </Button>
              <Button
                disabled={!staged}
                onClick={() => {
                  if (!staged) return;
                  onPick(staged);
                  closeSheet();
                }}
              >
                {t("new.upload")}
              </Button>
            </div>
          }
        >
          <div className="grid gap-1.5">
            <Label>{req(t("new.attachment"))}</Label>
            <div
              onClick={browse}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                take(e.dataTransfer.files[0]);
              }}
              className={cn(
                "border-line-strong relative flex cursor-pointer flex-col items-center",
                "justify-center gap-2 rounded-xl border border-dashed text-center",
                // Room around the invitation; out of the way of the picture
                // that answers it, which fills the panel it was dropped into.
                staged ? "p-2" : "px-16 py-8",
                over && "border-accent bg-accent-soft/30",
              )}
            >
              {staged ? (
                <StagedPicture file={staged} onRemove={() => setStaged(null)} />
              ) : (
                // The panel takes a click anywhere, which is what makes a
                // target of it; this is the same act with a place on the
                // keyboard, since in here it is the only way to a file.
                <button
                  type="button"
                  onClick={(e) => stopAnd(e, browse)}
                  className="flex flex-col items-center gap-2"
                >
                  <CloudUpload className="text-fg-subtle size-6" aria-hidden />
                  <span className="text-fg-muted text-callout">{dropMessage}</span>
                </button>
              )}
              {/* The other way to get a picture, on the same panel as the
                  first: the counter has the person in front of it either way.
                  It goes once there is a picture — what is wanted then is this
                  one gone, which is the delete standing on it. */}
              {staged ? null : (
                <button
                  type="button"
                  onClick={(e) => stopAnd(e, () => toCamera(true))}
                  className={cn(
                    "border-accent text-accent absolute top-1/2 right-5 grid size-10",
                    "-translate-y-1/2 place-items-center rounded-full border-2",
                    "hover:bg-accent-soft/40",
                  )}
                >
                  <Camera className="size-5" aria-hidden />
                  <span className="sr-only">{action}</span>
                </button>
              )}
              {/* Driven by the panel around it rather than reached on its own,
                  so it is out of the tab order and the accessibility tree. */}
              <input
                ref={file}
                type="file"
                accept="image/png,image/jpeg"
                tabIndex={-1}
                aria-hidden
                className="sr-only"
                onChange={(e) => {
                  take(e.target.files?.[0]);
                  // Cleared so that picking the same file twice is two picks: a
                  // card rescanned after a bad read is usually the same file.
                  e.target.value = "";
                }}
              />
            </div>
          </div>
        </Modal>
      ) : null}

      {/* The photograph's rules, before its camera. Legacy shows these once, as
          a step of their own, so nobody is reading them while the lens is live. */}
      {stage === "guide" ? (
        <Modal
          open
          onClose={() => setStage(viaSheet ? "sheet" : "none")}
          icon={<Camera className="size-5" aria-hidden />}
          title={action}
          description={t("new.guideBody")}
          size="sm"
          footerDivider={false}
          footer={
            <div className="flex w-full gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setStage(viaSheet ? "sheet" : "none")}
              >
                {t("common.cancel")}
              </Button>
              <Button className="flex-1" onClick={() => setStage("camera")}>
                {t("login.continue")}
              </Button>
            </div>
          }
        >
          <ul className="grid gap-3 py-2">
            {GUIDE_LINES.map((line) => (
              <li key={line} className="text-fg text-body flex items-center gap-2.5">
                <CheckCircle2 className="text-accent size-5 shrink-0" aria-hidden />
                <span>{t(line)}</span>
              </li>
            ))}
          </ul>
          <p className="text-fg-muted text-callout pb-2">{t("new.guideNote")}</p>
        </Modal>
      ) : null}

      {stage === "camera" ? (
        <CameraModal
          title={action}
          description={guide ? <GuidelinesNote /> : t("new.cameraIdBody")}
          onClose={() => setStage(viaSheet ? "sheet" : "none")}
          onCapture={(picture) => {
            // Back to the sheet it was opened from, so a capture is looked at
            // and said Upload to like any other picture; straight onto the
            // card when the camera was reached from the card's own link.
            if (viaSheet) {
              setStaged(picture);
              setStage("sheet");
              return;
            }
            setStage("none");
            onPick(picture);
          }}
        />
      ) : null}
    </div>
  );
}

/** The four rules, in the order legacy lists them. */
const GUIDE_LINES = ["new.guide1", "new.guide2", "new.guide3", "new.guide4"] as const;

/**
 * "…following the guidelines present…", where the middle word carries the four
 * rules on hover. The camera is already open by the time this is read, so the
 * rules are a reminder within reach rather than another step.
 */
function GuidelinesNote() {
  const t = useTranslation();
  const [before, after] = t("new.cameraPhotoBody").split("{guidelines}");
  return (
    <>
      {before}
      <Tooltip
        label={
          <span className="block w-56 list-disc whitespace-normal">
            <span className="grid gap-1.5">
              {GUIDE_LINES.map((line) => (
                <span key={line} className="block">
                  {t(line)}
                </span>
              ))}
            </span>
          </span>
        }
      >
        <span tabIndex={0} className="text-accent cursor-help font-semibold underline">
          {t("new.guidelines")}
        </span>
      </Tooltip>
      {after}
    </>
  );
}

/**
 * The picture waiting in the sheet, filling the panel it was dropped into,
 * with the way to think better of it standing on top of it.
 *
 * Its own component for the object URL: the picture is a `File` that has not
 * been read yet, and the URL standing for it has to be let go of when it is
 * replaced or the sheet closes, which is a lifetime and so a component.
 */
function StagedPicture({ file, onRemove }: { file: File; onRemove: () => void }) {
  const t = useTranslation();
  const [url, setUrl] = useState<string | null>(null);

  // Made in the effect and let go of in its cleanup, rather than worked out
  // while rendering: a URL that stands for a file is an outside resource with
  // a lifetime, and one made during a render that StrictMode throws away is
  // one nothing ever revokes — or worse, one the second run shows after the
  // first run's cleanup has already revoked it.
  useEffect(() => {
    const made = URL.createObjectURL(file);
    // eslint-disable-next-line react/set-state-in-effect
    setUrl(made);
    return () => URL.revokeObjectURL(made);
  }, [file]);

  return (
    <span className="group/staged relative block w-full">
      {url ? (
        <img
          src={url}
          alt=""
          className="bg-surface-sunken h-[200px] w-full rounded-lg object-cover"
        />
      ) : null}
      {/* The same delete the card puts over its picture, in the same place:
          out of the way until the pointer is on the picture it takes away. */}
      <button type="button" onClick={(e) => stopAnd(e, onRemove)} className={OVER_PICTURE}>
        <Trash2 className="size-4" aria-hidden />
        <span className="sr-only">{t("new.removePicture")}</span>
      </button>
    </span>
  );
}

/** Which lens: the one facing the clerk, or the one facing the counter. */
type Facing = "user" | "environment";

/**
 * Let a lens go, whether or not it has finished opening.
 *
 * A request still in flight cannot be called off, so it is awaited and then
 * turned off — otherwise the device is left on by a camera nobody is looking
 * at. A request that failed has nothing to turn off and is already spoken for
 * by the `setFault` beside it.
 */
function turnOff(opening: Promise<MediaStream>) {
  void opening.then(
    (stream) => stream.getTracks().forEach((track) => track.stop()),
    () => {},
  );
}

/**
 * The camera, for the card or the person in front of the counter.
 *
 * The stream is stopped on every way out — closing, capturing, unmounting —
 * because a camera that stays on after the modal is gone leaves the light on
 * and the clerk with no way to turn it off but reloading the page.
 */
function CameraModal({
  title,
  description,
  onClose,
  onCapture,
}: {
  title: string;
  description: ReactNode;
  onClose: () => void;
  onCapture: (file: File) => void;
}) {
  const t = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const [facing, setFacing] = useState<"user" | "environment">("environment");
  const [live, setLive] = useState(false);
  /**
   * Whether the lens was refused — the fact, not the sentence that says it.
   *
   * The sentence would have to be translated, and `t` is a new function on
   * every render — naming it below would put it in the effect's dependencies
   * and run the effect again each time this drew. Opening the camera draws,
   * which would tear the stream down and ask for it again: a camera that
   * stops the instant it starts, for as long as the sheet is up.
   */
  const [blocked, setBlocked] = useState(false);
  /**
   * No `mediaDevices` at all: the page is being served over plain http
   * somewhere that is not localhost, and no permission will fix that. Read
   * while rendering rather than found out in the effect — it is true of the
   * page, not of this camera, and it is true before anything is asked for.
   */
  const insecure = !navigator.mediaDevices?.getUserMedia;

  /**
   * The lens that is open, or on its way, and which way it faces.
   *
   * A ref, and the promise rather than the stream, because this component is
   * mounted twice: `StrictMode` sets every component up, tears it down and
   * sets it up again, and a second `getUserMedia` made while the first is
   * still opening the device leaves both of them hanging — a black viewfinder
   * and a Capture that never enables. The second run finds the first run's
   * request here and waits on that one instead of asking again. Legacy never
   * met this: it renders outside `StrictMode` (see `legacy/frontendx`), so
   * its camera is only ever asked for once.
   */
  const lens = useRef<{ facing: Facing; opening: Promise<MediaStream> } | null>(null);
  /** A turning-off waiting to happen, so the run that takes over can call it off. */
  const release = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only `facing` belongs here: the lens is opened once, and again when the
  // clerk turns it around, and at no other time.
  useEffect(() => {
    let cancelled = false;
    const element = video.current;

    // This run is taking the lens over from the one before it.
    if (release.current !== null) {
      clearTimeout(release.current);
      release.current = null;
    }
    // Except when it wants the other one, which the one open cannot become.
    if (lens.current && lens.current.facing !== facing) {
      turnOff(lens.current.opening);
      lens.current = null;
    }
    if (insecure) return;
    if (!lens.current) {
      lens.current = {
        facing,
        opening: navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } }),
      };
    }

    lens.current.opening.then(
      (stream) => {
        if (cancelled) return;
        if (element) element.srcObject = stream;
        setBlocked(false);
        setLive(true);
      },
      () => {
        if (!cancelled) setBlocked(true);
      },
    );

    return () => {
      cancelled = true;
      setLive(false);
      // Put off by a tick rather than done here. `StrictMode` tears this down
      // and sets it up again in the same breath, and a lens turned off in
      // between is one the second run adopts dead. Whichever run takes over
      // calls this off; if none does, the camera really is going away.
      release.current = setTimeout(() => {
        release.current = null;
        // The element holds on to a stopped stream and keeps its last frame
        // up, which reads as a camera that froze rather than one turned off.
        if (element) element.srcObject = null;
        if (lens.current) turnOff(lens.current.opening);
        lens.current = null;
      }, 0);
    };
  }, [facing, insecure]);

  const capture = () => {
    const source = video.current;
    if (!source || !source.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = source.videoWidth;
    canvas.height = source.videoHeight;
    canvas.getContext("2d")?.drawImage(source, 0, 0);
    canvas.toBlob((blob) => {
      // Named for what it is and when it was taken: a capture has no name of
      // its own, and `capture.jpg` on every one of them says no more than the
      // stored key does.
      if (blob) onCapture(new File([blob], capturedName(), { type: "image/jpeg" }));
    }, "image/jpeg");
  };

  return (
    <Modal
      open
      onClose={onClose}
      icon={<Camera className="size-5" aria-hidden />}
      title={title}
      description={description}
      size="sm"
      footerDivider={false}
      footer={
        <div className="flex w-full gap-3">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button className="flex-1" onClick={capture} disabled={!live}>
            {t("new.capture")}
          </Button>
        </div>
      }
    >
      <div className="relative h-72 overflow-hidden rounded-lg bg-black">
        <video ref={video} autoPlay playsInline muted className="size-full object-cover" />
        <button
          type="button"
          onClick={() => {
            // A refusal belongs to the lens it was given for, so turning the
            // camera around asks the other one with a clean slate.
            setBlocked(false);
            setFacing((was) => (was === "environment" ? "user" : "environment"));
          }}
          disabled={!live}
          className={cn(
            "absolute top-4 right-4 grid size-10 place-items-center rounded-full",
            "border-2 border-white/30 bg-black/60 text-white transition-all",
            "hover:border-white/50 hover:bg-black/80 disabled:opacity-50",
          )}
        >
          <SwitchCamera className="size-5" aria-hidden />
          <span className="sr-only">{t("new.switchCamera")}</span>
        </button>
      </div>
      {insecure || blocked ? (
        <FieldError text={t(insecure ? "new.cameraInsecure" : "new.cameraBlocked")} />
      ) : null}
    </Modal>
  );
}

/** One sentence under a field, in the danger colour, for a rule the field breaks. */
function FieldError({ text }: { text: string }) {
  return (
    <p role="status" className="text-danger text-callout mt-2 flex items-start gap-2">
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
      {text}
    </p>
  );
}

/** A link inside a sentence that opens something rather than going somewhere. */
function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-accent underline">
      {children}
    </button>
  );
}

function Choice({
  name,
  label,
  placeholder,
  options,
  required,
  error,
  v,
  set,
}: {
  name: string;
  label: ReactNode;
  placeholder: string;
  options: { value: string; label: string }[];
  required?: boolean;
  /** Said under the field, and the reason its outline is red. */
  error?: string | undefined;
  v: Record<string, string>;
  set: (name: string, value: string) => void;
}) {
  return (
    <Field label={label} htmlFor={name}>
      <OptionSelect
        id={name}
        value={v[name] ?? ""}
        onChange={(next) => set(name, next)}
        {...(required ? { required: true } : {})}
        invalid={Boolean(error)}
        placeholder={placeholder}
        choices={options}
      />
      {error ? <FieldError text={error} /> : null}
    </Field>
  );
}

function Check({
  name,
  label,
  error,
  v,
  set,
}: {
  name: string;
  label: ReactNode;
  /** Said under the box, for an agreement the form will not go without. */
  error?: string | undefined;
  v: Record<string, string>;
  set: (name: string, value: string) => void;
}) {
  if (error) {
    return (
      <div>
        <Check name={name} label={label} v={v} set={set} />
        <FieldError text={error} />
      </div>
    );
  }
  return (
    <label className="text-fg text-body flex items-start gap-3">
      <input
        type="checkbox"
        className="accent-accent mt-1 size-5 shrink-0"
        checked={v[name] === "true"}
        onChange={(e) => set(name, e.target.checked ? "true" : "false")}
      />
      <span>{label}</span>
    </label>
  );
}

export default NewMemberPage;
